/**
 * TanStack Query hooks for the rules CRUD + versioning + test-run surfaces
 * (#53, #55, #56, #118, #119).
 *
 * All endpoints live behind the API's `/rules/*` namespace; the cookie-based
 * auth flow happens transparently via `apiFetch`'s `credentials: 'include'`
 * default. Mutation hooks invalidate the impacted list/detail caches so the
 * UI re-renders without an explicit refetch at the call site.
 */
import {
  type CreateRuleRequest,
  type RollbackRuleRequest,
  RollbackRuleResponseSchema,
  type RuleDraft,
  type RuleListItem,
  RuleListResponseSchema,
  type RuleResponse,
  RuleResponseSchema,
  type RuleScope,
  type RuleVersionListResponse,
  RuleVersionListResponseSchema,
  type TestRunRuleResponse,
  TestRunRuleResponseSchema,
  type UpdateRuleRequest,
} from '@invoice/shared/schemas';
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiFetch } from '../api';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

/**
 * Stable query keys that the mutation hooks invalidate. Generic-scope and
 * customer-scope lists live under separate cache entries because the API
 * returns disjoint sets and refetching one does not stale the other.
 */
export const RULES_LIST_KEY = ['rules', 'list'] as const;
export const ruleVersionsKey = (ruleId: string) => ['rules', 'versions', ruleId] as const;

interface RulesListFilter {
  scope: RuleScope;
  customerId?: string | undefined;
  supplierName?: string | undefined;
}

function rulesListKey(filter: RulesListFilter) {
  return [
    ...RULES_LIST_KEY,
    filter.scope,
    filter.customerId ?? null,
    filter.supplierName ?? null,
  ] as const;
}

// ---------------------------------------------------------------------------
// GET /rules
// ---------------------------------------------------------------------------

export async function fetchRulesList(
  filter: RulesListFilter,
  signal?: AbortSignal,
): Promise<RuleListItem[]> {
  const res = await apiFetch('/rules', {
    schema: RuleListResponseSchema,
    searchParams: {
      scope: filter.scope,
      customerId: filter.customerId,
      supplierName: filter.supplierName,
    },
    ...(signal ? { signal } : {}),
  });
  return res.items;
}

export function useRulesList(filter: RulesListFilter): UseQueryResult<RuleListItem[], Error> {
  return useQuery({
    queryKey: rulesListKey(filter),
    queryFn: ({ signal }) => fetchRulesList(filter, signal),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// POST /rules
// ---------------------------------------------------------------------------

export async function createRule(input: CreateRuleRequest): Promise<RuleResponse> {
  return apiFetch('/rules', {
    method: 'POST',
    json: input,
    schema: RuleResponseSchema,
  });
}

export function useCreateRule(): UseMutationResult<RuleResponse, Error, CreateRuleRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRule,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RULES_LIST_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// PUT /rules/:id
// ---------------------------------------------------------------------------

interface UpdateRuleVars {
  id: string;
  patch: UpdateRuleRequest;
}

export async function updateRule(input: UpdateRuleVars): Promise<RuleResponse> {
  return apiFetch(`/rules/${encodeURIComponent(input.id)}`, {
    method: 'PUT',
    json: input.patch,
    schema: RuleResponseSchema,
  });
}

export function useUpdateRule(): UseMutationResult<RuleResponse, Error, UpdateRuleVars> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateRule,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: RULES_LIST_KEY });
      void queryClient.invalidateQueries({ queryKey: ruleVersionsKey(data.id) });
    },
  });
}

// ---------------------------------------------------------------------------
// DELETE /rules/:id
// ---------------------------------------------------------------------------

export async function deleteRule(id: string): Promise<void> {
  await apiFetch(`/rules/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function useDeleteRule(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteRule,
    onSuccess: (_data, id) => {
      queryClient.setQueriesData<RuleListItem[]>(
        { queryKey: RULES_LIST_KEY },
        (current) => current?.filter((item) => item.id !== id) ?? current,
      );
      void queryClient.invalidateQueries({ queryKey: RULES_LIST_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// GET /rules/:id/versions  (#55, #119)
// ---------------------------------------------------------------------------

export async function fetchRuleVersions(
  id: string,
  signal?: AbortSignal,
): Promise<RuleVersionListResponse> {
  return apiFetch(`/rules/${encodeURIComponent(id)}/versions`, {
    schema: RuleVersionListResponseSchema,
    ...(signal ? { signal } : {}),
  });
}

export function useRuleVersions(id: string | null): UseQueryResult<RuleVersionListResponse, Error> {
  return useQuery({
    queryKey: ruleVersionsKey(id ?? '__none__'),
    queryFn: ({ signal }) => {
      // The hook is gated by `enabled` below, so `id` is non-null here.
      // The non-null assertion is fenced by the `enabled` flag.
      // reason: react-query queryFn is invoked only when enabled=true.
      return fetchRuleVersions(id as string, signal);
    },
    enabled: typeof id === 'string' && id.length > 0,
    staleTime: 10_000,
  });
}

// ---------------------------------------------------------------------------
// POST /rules/:id/rollback  (#55, #119)
// ---------------------------------------------------------------------------

interface RollbackVars {
  id: string;
  body: RollbackRuleRequest;
}

export async function rollbackRule(input: RollbackVars) {
  return apiFetch(`/rules/${encodeURIComponent(input.id)}/rollback`, {
    method: 'POST',
    json: input.body,
    schema: RollbackRuleResponseSchema,
  });
}

export function useRollbackRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: rollbackRule,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: RULES_LIST_KEY });
      void queryClient.invalidateQueries({ queryKey: ruleVersionsKey(data.ruleId) });
    },
  });
}

// ---------------------------------------------------------------------------
// POST /rules/test-run  (#56)
// ---------------------------------------------------------------------------

interface TestRunRuleInput {
  ruleDraft: RuleDraft;
  file: File;
}

export async function testRunRule(input: TestRunRuleInput): Promise<TestRunRuleResponse> {
  const formData = new FormData();
  formData.append('file', input.file, input.file.name);
  formData.append('ruleDraft', JSON.stringify(input.ruleDraft));

  return apiFetch('/rules/test-run', {
    method: 'POST',
    body: formData,
    schema: TestRunRuleResponseSchema,
  });
}

export function useTestRunRule(): UseMutationResult<TestRunRuleResponse, Error, TestRunRuleInput> {
  return useMutation({ mutationFn: testRunRule });
}
